import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Badge, Layout, Menu, Typography, Select, Divider, Button, Drawer, Space, Tooltip, message } from "antd";
import {
    AlertOutlined,
    ApartmentOutlined,
    BookOutlined,
    DashboardOutlined,
    GlobalOutlined,
    IdcardOutlined,
    LineChartOutlined,
    LogoutOutlined,
    ReloadOutlined,
    SettingOutlined,
    TeamOutlined,
    ToolOutlined
} from "@ant-design/icons";

import LoginPage from "./components/LoginPage";
import ObserverTerminalPage from "./components/ObserverTerminalPage";
import PerfDebugOverlay from "./components/PerfDebugOverlay";
import { authHeaders, fetchTenantPermissions, getRole, hasPermission, isPlatformAdminRole, roleDisplayName } from "./lib/permissions";
import { resolveApiBase } from "./lib/apiBase";
import { isRetryableOutage, shouldThrottleRetry } from "./lib/resilientFetch";
import { installSessionExpiryInterceptor, rememberPostLoginRedirect, SESSION_EXPIRED_EVENT } from "./lib/sessionExpiry";
import { perfLog, perfMarkEnd, perfMarkStart } from "./lib/perfDebug";

installSessionExpiryInterceptor();

const SystemDashboard = lazy(() => import("./components/SystemDashboard"));
const IncidentPlaybooksPanel = lazy(() => import("./components/IncidentPlaybooksPanel"));
const RemediationCenter = lazy(() => import("./components/RemediationCenter"));
const TicketsPanel = lazy(() => import("./components/TicketsPanel"));
const AlertsAnomaliesPanel = lazy(() => import("./components/AlertsAnomaliesPanel"));
const TenantManagement = lazy(() => import("./components/TenantManagement"));
const TenantReportsWorkspace = lazy(() => import("./components/TenantReportsWorkspace"));
const TenantDashboard = lazy(() => import("./components/TenantDashboard"));
const PlatformHealthDashboard = lazy(() => import("./components/PlatformHealthDashboard"));
const AccountProfile = lazy(() => import("./components/AccountProfile"));
const SetPasswordPage = lazy(() => import("./components/SetPasswordPage"));
const NotificationCenter = lazy(() => import("./components/NotificationCenter"));
const StickyKnowledgeDock = lazy(() => import("./components/StickyKnowledgeDock"));
const ApprovalActionPage = lazy(() => import("./components/ApprovalActionPage"));
const AnalysisConsolePage = lazy(() => import("./components/AnalysisConsolePage"));
const AnalysisConsolesWorkspace = lazy(() => import("./components/AnalysisConsolesWorkspace"));
const TenantDashboardBuilder = lazy(() => import("./components/TenantDashboardBuilder"));

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE = resolveApiBase();

class RouteChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorMessage: "" };
    }

    shouldAutoReload(error) {
        const message = String(error?.message || "");
        return (
            message.includes("before initialization") ||
            message.includes("Failed to fetch dynamically imported module") ||
            message.includes("Importing a module script failed")
        );
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            errorMessage: error?.message || "Failed to load this page."
        };
    }

    componentDidCatch(error) {
        console.error("Route chunk load failed", error);

        if (!this.shouldAutoReload(error) || typeof window === "undefined") {
            return;
        }

        const pathname = window.location.pathname || "unknown";
        const reloadKey = `datasnare:route-reload:${pathname}`;

        try {
            if (window.sessionStorage.getItem(reloadKey) === "1") {
                return;
            }
            window.sessionStorage.setItem(reloadKey, "1");
        } catch {
            return;
        }

        window.location.reload();
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div style={{ padding: 24, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                <Title level={4} style={{ marginTop: 0 }}>Page Load Error</Title>
                <Text type="secondary">
                    A page module failed to load. This usually happens after a deployment or container restart.
                </Text>
                <div style={{ marginTop: 12 }}>
                    <Text type="danger">{this.state.errorMessage}</Text>
                </div>
                <Space style={{ marginTop: 16 }}>
                    <Button type="primary" onClick={() => window.location.reload()}>
                        Reload Application
                    </Button>
                </Space>
            </div>
        );
    }
}

const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [authenticated, setAuthenticated] = useState(false);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [username, setUsername] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [selectedSystem, setSelectedSystem] = useState("");
    const [tenants, setTenants] = useState([]);
    const [loadingTenants, setLoadingTenants] = useState(false);
    const [tenantPermissions, setTenantPermissions] = useState([]);
    const [knowledgeDockOpen, setKnowledgeDockOpen] = useState(false);
    const [knowledgeDockPinCount, setKnowledgeDockPinCount] = useState(0);
    const [tenantFeedUnavailable, setTenantFeedUnavailable] = useState(false);
    const tenantOutageNotifiedRef = useRef(false);
    const lastTenantFetchFailureAtRef = useRef(0);
    const tenantFailureCountRef = useRef(0);
    const tenantLoadInFlightRef = useRef(false);
    const didInitialTenantLoadRef = useRef(false);
    const tenantDropdownOpenAtRef = useRef(null);

    // Check authentication on app load
    useEffect(() => {
        const actor = localStorage.getItem("actor");
        const role = getRole();
        const user = localStorage.getItem("username");
        setUsername(user);
        setAuthenticated(!!(actor && role));
        setLoadingAuth(false);
    }, []);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!loadingAuth && !authenticated && location.pathname !== "/login" && location.pathname !== "/set-password" && location.pathname !== "/approval-action" && !location.pathname.startsWith("/analysis-console/")) {
            navigate("/login");
        }
    }, [authenticated, loadingAuth, location.pathname, navigate]);

    const handleLogout = () => {
        localStorage.removeItem("actor");
        localStorage.removeItem("role");
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        setAuthenticated(false);
        setUsername(null);
        setSelectedTenant(null);
        message.success("Logged out");
        navigate("/login");
    };

    // When the Bearer session expires (8h TTL), every API call starts failing
    // with 401. Detect it once, clear the dead session, and send the user back
    // to the login page - returning them to this page after they sign in.
    useEffect(() => {
        const onSessionExpired = () => {
            if (!localStorage.getItem("token")) return; // already handled
            rememberPostLoginRedirect(`${location.pathname}${location.search || ""}`);
            localStorage.removeItem("actor");
            localStorage.removeItem("role");
            localStorage.removeItem("token");
            localStorage.removeItem("username");
            setAuthenticated(false);
            setUsername(null);
            message.warning("Your session has expired. Please sign in again.", 6);
            navigate("/login");
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, [navigate, location.pathname, location.search]);

    // Load tenants on mount
    useEffect(() => {
        loadTenants({ preserveSelection: true, autoSelectFirst: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load tenants once a session is established (mount-time load is skipped
    // when the user starts on the login page without a token).
    useEffect(() => {
        if (authenticated) loadTenants({ preserveSelection: true, autoSelectFirst: false, silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated]);

    // Reload tenants on route change
    useEffect(() => {
        if (!didInitialTenantLoadRef.current) {
            didInitialTenantLoadRef.current = true;
            return;
        }
        loadTenants({ preserveSelection: true, autoSelectFirst: false, silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Reload tenants on window focus
    useEffect(() => {
        const onFocus = () => loadTenants({ preserveSelection: true, autoSelectFirst: false, silent: true });
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadTenantPermissions = async () => {
            if (!authenticated) {
                setTenantPermissions([]);
                return;
            }
            const permissions = await fetchTenantPermissions(selectedTenant?.id || null);
            if (!cancelled) {
                setTenantPermissions(permissions);
            }
        };

        loadTenantPermissions();
        return () => {
            cancelled = true;
        };
    }, [authenticated, selectedTenant?.id]);

    useEffect(() => {
        if (!tenants.length) return;
        const params = new URLSearchParams(location.search || "");
        const tenantIdParam = params.get("tenantId");
        if (!tenantIdParam) return;
        const targetId = Number(tenantIdParam);
        if (!Number.isFinite(targetId)) return;
        const linkedTenant = tenants.find((row) => Number(row.id) === targetId);
        if (!linkedTenant) return;
        setSelectedTenant((prev) => (Number(prev?.id) === targetId ? prev : linkedTenant));
    }, [location.search, tenants]);

    const loadTenants = async ({ preserveSelection = true, autoSelectFirst = false, silent = false } = {}) => {
        const perfToken = perfMarkStart("app.load_tenants", {
            preserveSelection,
            autoSelectFirst,
            silent,
            hasCurrentSelection: Boolean(selectedTenant?.id),
        });
        // Never fetch tenants without a session (e.g. on the login page after a
        // logout or expiry) - it can only produce 401 noise.
        if (!localStorage.getItem("token")) {
            perfMarkEnd(perfToken, { result: "skipped_unauthenticated" });
            return;
        }
        if (tenantLoadInFlightRef.current) {
            perfMarkEnd(perfToken, { result: "skipped_inflight" });
            return;
        }
        if (silent && shouldThrottleRetry(lastTenantFetchFailureAtRef.current, tenantFailureCountRef.current, { baseMs: 5000, maxMs: 120000, jitterMs: 1000 })) {
            perfMarkEnd(perfToken, { result: "skipped_throttled" });
            return;
        }

        tenantLoadInFlightRef.current = true;

        if (!silent) setLoadingTenants(true);

        try {
            const res = await fetch(`${API_BASE}/tenants`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Failed to load tenants (${res.status})`);
            const data = await res.json();
            const rows = Array.isArray(data) ? data : [];
            setTenants(rows);
            setTenantFeedUnavailable(false);
            tenantOutageNotifiedRef.current = false;
            tenantFailureCountRef.current = 0;
            lastTenantFetchFailureAtRef.current = 0;

            setSelectedTenant((prev) => {
                if (preserveSelection && prev?.id) {
                    const refreshed = rows.find((tenant) => tenant.id === prev.id);
                    if (refreshed) return refreshed;
                }
                if (autoSelectFirst && rows.length > 0) return rows[0];
                return prev;
            });
            perfMarkEnd(perfToken, {
                result: "success",
                tenantCount: rows.length,
            });
        } catch (err) {
            const isGatewayOutage = isRetryableOutage(err);
            lastTenantFetchFailureAtRef.current = Date.now();
            tenantFailureCountRef.current += 1;
            setTenantFeedUnavailable(true);

            if (!tenantOutageNotifiedRef.current) {
                message.warning("Tenant service is temporarily unavailable. Showing cached tenant context.");
                tenantOutageNotifiedRef.current = true;
            }

            if (!isGatewayOutage) {
                console.error("Failed to load tenants", err);
                if (!silent) message.error("Failed to load tenants");
            }
            perfMarkEnd(perfToken, {
                result: isGatewayOutage ? "gateway_outage" : "error",
                message: err?.message || String(err),
            });
        } finally {
            tenantLoadInFlightRef.current = false;
            if (!silent) setLoadingTenants(false);
        }
    };

    const tenantOptions = useMemo(
        () => tenants.map((tenant) => ({ label: tenant.tenant_name, value: tenant.id })),
        [tenants]
    );

    useEffect(() => {
        if (tenantDropdownOpenAtRef.current == null) return;
        perfLog("app.tenant_dropdown_populate", {
            optionCount: tenantOptions.length,
            timeToOptionsMs: Number((performance.now() - tenantDropdownOpenAtRef.current).toFixed(2)),
        });
        tenantDropdownOpenAtRef.current = null;
    }, [tenantOptions]);

    const handleTenantChange = useCallback((value) => {
        const tenantObj = tenants.find((tenant) => tenant.id === value) || null;
        setSelectedTenant(tenantObj);
        setSelectedSystem("");
    }, [tenants]);

    const handleTenantObjectChange = (tenantObj) => {
        setSelectedTenant(tenantObj || null);
        setSelectedSystem("");
    };

    const selectedTenantId = selectedTenant?.id || null;
    const routeFallback = <div style={{ textAlign: "center", padding: "30px" }}>Loading page...</div>;
    const canOpenKnowledgeDock = Boolean(selectedTenantId) && location.pathname !== "/login";
    const knowledgeDockMode = location.pathname === "/" && selectedSystem ? "system" : "tenant";
    const dockScope = knowledgeDockMode === "system" ? `system:${selectedSystem || "unknown"}` : "tenant";
    const dockContextKey = `tenant:${selectedTenantId || "none"}:${dockScope}`;
    const pinsStorageKey = `datasnare:dock:pins:${dockContextKey}`;
    const role = getRole();
    const canViewPlatform = isPlatformAdminRole(role);
    const canManageTenantRbac = hasPermission(tenantPermissions, "rbac.manage");
    const canViewTenantAdmin = selectedTenantId ? hasPermission(tenantPermissions, "rbac.view") || canManageTenantRbac : true;
    const selectedMenuKey = location.pathname.startsWith("/reports")
        ? "/reports"
        : location.pathname.startsWith("/patterns")
            ? "/patterns"
        : location.pathname.startsWith("/analysis-consoles")
            ? "/analysis-consoles"
        : location.pathname.startsWith("/dashboards")
            ? "/dashboards"
        : location.pathname.startsWith("/dashboard-builder")
            ? "/dashboard-builder"
        : location.pathname.startsWith("/alerts")
            ? "/alerts-summary"
            : location.pathname;
    const menuItems = [
        {
            type: "group",
            label: "Operations",
            children: [
                { key: "/", icon: <DashboardOutlined />, label: "System Dashboard" },
                { key: "/tenant-dashboard", icon: <TeamOutlined />, label: "Tenant Dashboard" },
                { key: "/alerts-summary", icon: <AlertOutlined />, label: "Alerts & Anomalies" },
                { key: "/patterns", icon: <AlertOutlined />, label: "Pattern Review" },
                { key: "/analysis-consoles", icon: <TeamOutlined />, label: "Analysis Consoles" },
                { key: "/dashboards", icon: <DashboardOutlined />, label: "Dashboards" },
                { key: "/dashboard-builder", icon: <DashboardOutlined />, label: "Dashboard Builder" },
                { key: "/remediation", icon: <ToolOutlined />, label: "Remediation" }
            ]
        },
        { type: "divider" },
        {
            type: "group",
            label: "Administration",
            children: [
                { key: "/tickets", icon: <SettingOutlined />, label: "Tickets" },
                ...(canViewTenantAdmin ? [{ key: "/tenants", icon: <ApartmentOutlined />, label: "Tenant Management" }] : []),
                ...(canViewTenantAdmin ? [{ key: "/reports", icon: <LineChartOutlined />, label: "Reports" }] : []),
                ...(canViewPlatform ? [{ key: "/platform", icon: <GlobalOutlined />, label: "Platform Management" }] : [])
            ]
        },
        { type: "divider" },
        { key: "/account", icon: <IdcardOutlined />, label: "My Account" },
        { type: "divider" },
        { key: "/logout", icon: <LogoutOutlined />, label: "Logout" }
    ];

    useEffect(() => {
        if (!canOpenKnowledgeDock || typeof window === "undefined") {
            setKnowledgeDockPinCount(0);
            return;
        }

        const refreshCount = () => {
            try {
                const raw = window.localStorage.getItem(pinsStorageKey);
                const parsed = JSON.parse(String(raw || "[]"));
                setKnowledgeDockPinCount(Array.isArray(parsed) ? parsed.length : 0);
            } catch {
                setKnowledgeDockPinCount(0);
            }
        };

        const onStorage = (event) => {
            if (event.key === pinsStorageKey) refreshCount();
        };

        const onPinsUpdated = (event) => {
            const detail = event?.detail || {};
            if (detail.key === pinsStorageKey) {
                setKnowledgeDockPinCount(Number.isFinite(Number(detail.count)) ? Number(detail.count) : 0);
            }
        };

        refreshCount();
        window.addEventListener("storage", onStorage);
        window.addEventListener("datasnare:dock-pins-updated", onPinsUpdated);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("datasnare:dock-pins-updated", onPinsUpdated);
        };
    }, [canOpenKnowledgeDock, pinsStorageKey]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const onKeyDown = (event) => {
            if (!canOpenKnowledgeDock) return;
            if (!(event.ctrlKey || event.metaKey) || String(event.key || "").toLowerCase() !== "k") return;

            const target = event.target;
            const tagName = String(target?.tagName || "").toLowerCase();
            const isTypingField =
                tagName === "input" ||
                tagName === "textarea" ||
                target?.isContentEditable;
            if (isTypingField) return;

            event.preventDefault();
            setKnowledgeDockOpen((prev) => !prev);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [canOpenKnowledgeDock]);

    useEffect(() => {
        if (!canOpenKnowledgeDock || typeof window === "undefined") return;

        const hintKey = "datasnare:knowledge-dock-hint:v1";
        try {
            if (window.sessionStorage.getItem(hintKey) === "1") return;
            window.sessionStorage.setItem(hintKey, "1");
        } catch {
            return;
        }

        message.info({
            content: "Tip: Open Knowledge Dock from the header or press Ctrl+K.",
            duration: 5,
        });
    }, [canOpenKnowledgeDock]);

    // Conditional render based on auth state
    if (loadingAuth) {
        return <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>;
    }

    if (location.pathname === "/terminal-observer") {
        return <ObserverTerminalPage />;
    }

    // Share links are guest surfaces: never render the authenticated app shell around them.
    if (location.pathname.startsWith("/analysis-console/")) {
        return (
            <Routes>
                <Route path="/analysis-console/:token" element={<AnalysisConsolePage />} />
            </Routes>
        );
    }

    if (!authenticated) {
        return <Routes><Route path="/login" element={<LoginPage onLoginSuccess={() => { setUsername(localStorage.getItem("username")); setAuthenticated(true); }} />} /><Route path="/set-password" element={<SetPasswordPage />} /><Route path="/approval-action" element={<ApprovalActionPage />} /><Route path="/analysis-console/:token" element={<AnalysisConsolePage />} /><Route path="/terminal-observer" element={<ObserverTerminalPage />} /><Route path="*" element={<LoginPage onLoginSuccess={() => { setUsername(localStorage.getItem("username")); setAuthenticated(true); }} />} /></Routes>;
    }

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sider width={260} theme="dark">
                <div style={{ padding: "20px", textAlign: "center", background: "#0f172a" }}>
                    <h1 style={{ color: "white", margin: 0 }}>DataSnare-AIOps</h1>
                    {username && (
                        <Text style={{ color: "#94a3b8", fontSize: 12 }}>
                            {username} ({roleDisplayName(role)})
                        </Text>
                    )}
                </div>

                <div style={{ padding: "16px" }}>
                    <label style={{ color: "#94a3b8" }}>Tenant</label>
                    <Space.Compact style={{ width: "100%" }}>
                        <Select
                            placeholder="Select Tenant"
                            style={{ width: "100%" }}
                            value={selectedTenantId || undefined}
                            onChange={handleTenantChange}
                            onOpenChange={(open) => {
                                if (open) {
                                    tenantDropdownOpenAtRef.current = performance.now();
                                    perfLog("app.tenant_dropdown_open", { optionCount: tenantOptions.length });
                                    return;
                                }
                                tenantDropdownOpenAtRef.current = null;
                            }}
                            loading={loadingTenants}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={tenantOptions}
                            virtual
                        />
                        <Button icon={<ReloadOutlined />} loading={loadingTenants} onClick={() => loadTenants({ preserveSelection: true, autoSelectFirst: false })} />
                    </Space.Compact>
                </div>

                <Divider style={{ margin: "8px 16px", borderColor: "rgba(255,255,255,0.15)" }} />

                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[selectedMenuKey]}
                    items={menuItems}
                    onClick={({ key }) => {
                        if (key === "/logout") {
                            handleLogout();
                            return;
                        }
                        navigate(key);
                    }}
                />
            </Sider>

            <Layout>
                <Header style={{ background: "#fff", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
                    <Title level={4} style={{ margin: 0, lineHeight: "64px" }}>Monitoring Platform</Title>
                    <Space align="center">
                        <Badge count={knowledgeDockPinCount} overflowCount={99} size="small" showZero={false}>
                            <Tooltip title="Open Knowledge Dock (Ctrl+K)">
                                <Button
                                    icon={<BookOutlined />}
                                    onClick={() => setKnowledgeDockOpen(true)}
                                    disabled={!canOpenKnowledgeDock}
                                >
                                    Knowledge Dock
                                </Button>
                            </Tooltip>
                        </Badge>
                        <RouteChunkErrorBoundary>
                            <Suspense fallback={null}>
                                <NotificationCenter tenantId={selectedTenantId} />
                            </Suspense>
                        </RouteChunkErrorBoundary>
                        {username ? <Text type="secondary">{username}</Text> : null}
                    </Space>
                </Header>

                {tenantFeedUnavailable ? (
                    <div
                        style={{
                            background: "#fffbe6",
                            borderBottom: "1px solid #ffe58f",
                            color: "#8c6d1f",
                            padding: "6px 20px",
                            fontSize: 12,
                        }}
                    >
                        Tenant feed is temporarily unavailable. Some tenant data may be stale until service recovers.
                    </div>
                ) : null}

                <Content style={{ margin: "20px" }}>
                    <RouteChunkErrorBoundary>
                        <Suspense fallback={routeFallback}>
                            <Routes>
                                <Route path="/" element={<SystemDashboard tenantId={selectedTenantId} tenantPermissions={tenantPermissions} selectedSystem={selectedSystem} onSystemChange={setSelectedSystem} />} />

                                <Route path="/tenant-dashboard" element={<TenantDashboard selectedTenantId={selectedTenantId} onTenantChange={handleTenantObjectChange} onSystemChange={setSelectedSystem} />} />

                                <Route path="/tenant-agents" element={<TenantDashboard selectedTenantId={selectedTenantId} onTenantChange={handleTenantObjectChange} onSystemChange={setSelectedSystem} initialTab="agents" />} />

                                <Route path="/alerts" element={selectedTenantId ? <AlertsAnomaliesPanel tenantId={selectedTenantId} systemName={selectedSystem} onSystemChange={setSelectedSystem} /> : <div>Select a tenant first</div>} />

                                <Route path="/alerts-summary" element={selectedTenantId ? <AlertsAnomaliesPanel tenantId={selectedTenantId} systemName={selectedSystem} onSystemChange={setSelectedSystem} /> : <div>Select a tenant first</div>} />

                                <Route path="/patterns" element={selectedTenantId ? <AlertsAnomaliesPanel tenantId={selectedTenantId} systemName={selectedSystem} onSystemChange={setSelectedSystem} initialTabKey="patterns" /> : <div>Select a tenant first</div>} />

                                <Route path="/analysis-consoles" element={selectedTenantId ? <AnalysisConsolesWorkspace tenantId={selectedTenantId} tenantName={selectedTenant?.tenant_name} /> : <div>Select a tenant first</div>} />

                                <Route path="/dashboards" element={selectedTenantId ? <TenantDashboardBuilder tenantId={selectedTenantId} tenantPermissions={tenantPermissions} viewerOnly /> : <div>Select a tenant first</div>} />

                                <Route path="/dashboard-builder" element={selectedTenantId ? <TenantDashboardBuilder tenantId={selectedTenantId} tenantPermissions={tenantPermissions} /> : <div>Select a tenant first</div>} />

                                <Route path="/remediation" element={selectedTenantId ? <RemediationCenter tenantId={selectedTenantId} systemName={selectedSystem} onSystemChange={setSelectedSystem} /> : <div>Select a tenant first</div>} />

                                <Route path="/tickets" element={<TicketsPanel tenantId={selectedTenantId} />} />

                                <Route path="/tenants" element={<TenantManagement selectedTenantId={selectedTenantId} tenantPermissions={tenantPermissions} onTenantChange={(tenantId) => { setSelectedTenant({ id: tenantId }); setSelectedSystem(""); }} onSystemChange={setSelectedSystem} /> } />

                                <Route path="/reports" element={<TenantReportsWorkspace tenantId={selectedTenantId} tenantName={selectedTenant?.tenant_name} onOpenTenantManagement={() => navigate("/tenants")} />} />

                                <Route path="/reports/:family" element={<TenantReportsWorkspace tenantId={selectedTenantId} tenantName={selectedTenant?.tenant_name} onOpenTenantManagement={() => navigate("/tenants")} />} />

                                <Route path="/platform" element={<PlatformHealthDashboard />} />

                                <Route path="/account" element={<AccountProfile />} />

                                <Route path="/set-password" element={<SetPasswordPage />} />

                                <Route path="/approval-action" element={<ApprovalActionPage />} />
                            </Routes>

                            {location.pathname === "/" && selectedSystem && (
                                <div style={{ marginTop: 16 }}>
                                    <IncidentPlaybooksPanel tenantId={selectedTenantId} systemName={selectedSystem} />
                                </div>
                            )}
                        </Suspense>
                    </RouteChunkErrorBoundary>
                </Content>

                <Drawer
                    title="Knowledge Dock"
                    placement="right"
                    width={500}
                    open={knowledgeDockOpen}
                    onClose={() => setKnowledgeDockOpen(false)}
                    destroyOnClose={false}
                >
                    <RouteChunkErrorBoundary>
                        <Suspense fallback={routeFallback}>
                            {canOpenKnowledgeDock ? (
                                <StickyKnowledgeDock
                                    tenantId={selectedTenantId}
                                    tenantName={selectedTenant?.tenant_name || "Tenant"}
                                    systemName={knowledgeDockMode === "system" ? selectedSystem : ""}
                                    mode={knowledgeDockMode}
                                    onOpenIntegrations={() => {
                                        navigate("/tenants", { state: { initialTab: "integrations", tenantId: selectedTenantId } });
                                        setKnowledgeDockOpen(false);
                                    }}
                                />
                            ) : (
                                <div>Select a tenant to use the Knowledge Dock.</div>
                            )}
                        </Suspense>
                    </RouteChunkErrorBoundary>
                </Drawer>
                <PerfDebugOverlay />
            </Layout>
        </Layout>
    );
};

const App = () => (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
    </Router>
);

export default App;
